import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  Loader2,
  Pencil,
  Search,
  UserPlus,
  X,
} from "lucide-react";
import {
  addClientUser,
  checkEmailExists,
  editClientUser,
  getUsers,
  setUserStatus,
} from "../services/userService";
import {
  describeUnsupportedUserWrite,
  resolveUserWriteEndpoints,
} from "../services/userEndpoints";
import { getSourceName } from "../services/authService";
import { getClientEntry } from "../config/clientConfig";
import {
  ROLES,
  accessLevelForRole,
  initialsOf,
  permissionLabelsFor,
  toUserRows,
} from "../utils/userRoles";
import { filterUsers, formatModified } from "./peopleList";

/**
 * People, in the Clearline design.
 *
 * The redesigned sidebar used to link People at /people, which is the old
 * page — so the new design framed the old chrome and the seam showed. This is
 * the same feature in the same vocabulary as the redesigned case list: page
 * shell, one card, a grid header, status pills, the accent button.
 *
 * Written as a new page rather than a restyle of src/pages/UserManagement.js on
 * purpose. That file is where the two designs would collide, and it is being
 * edited right now by the per-client-endpoints work; both designs keep their
 * own page, exactly as the cases list does.
 *
 * Password: most clients' add-user route takes none, so the field used to be
 * dropped before sending. Hegic's /add_user is the opposite — it requires one
 * — and that client carries a `userForm` entry in its own config (see
 * hegic.json) naming its own roles and permission checkboxes, which is what
 * turns the password/confirm-password/permissions fields on below.
 */

const shell =
  "flex h-full min-h-0 w-full flex-col bg-cl-bg px-4 py-4 sm:px-8 sm:py-[26px] font-clearline text-cl-text";
const control =
  "rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] text-[12.5px] text-cl-secondary outline-none transition-colors focus:border-cl-accent";
// Native <select> renders the OS's own arrow glyph — inconsistent with the
// rest of the app's custom-styled dropdowns. appearance-none drops it; this
// draws the same solid triangle used everywhere else instead.
const selectArrowStyle = {
  appearance: "none",
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpolygon points='0,0 10,0 5,6' fill='%235b6572'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 11px center",
  backgroundSize: "10px 6px",
};
const selectClass = `${control} appearance-none pr-7`;
const GRID_COLS = ["38px", "1.4fr", "1.6fr", "1.2fr", "110px", "150px"];
const GRID_COLS_WITH_PERMISSIONS = ["38px", "1.2fr", "1.4fr", "1fr", "1.4fr", "110px", "130px"];

// The warn banner's colours are tokens without Tailwind utilities behind them —
// bg-cl-warn-banner and friends do not exist in tailwind.config.js, so writing
// them as classes renders nothing at all.
const WARN_BOX = "flex items-start gap-2 rounded-tile border px-3 py-[9px]";
const WARN_STYLE = {
  background: "var(--cl-warn-banner-bg)",
  borderColor: "var(--cl-warn-banner-border)",
};

const STATUS = {
  active: { label: "Active", bg: "var(--cl-success-chip-bg)", fg: "var(--cl-success-chip-fg)" },
  inactive: {
    label: "Inactive",
    bg: "var(--cl-status-processing-bg)",
    fg: "var(--cl-status-processing-fg)",
  },
};

function StatusPill({ active }) {
  const chip = active ? STATUS.active : STATUS.inactive;
  return (
    <span
      className="inline-flex items-center gap-[5px] rounded-full px-[9px] py-[3px] text-[11.5px] font-semibold"
      style={{ background: chip.bg, color: chip.fg }}
    >
      <span
        className="h-[5px] w-[5px] rounded-full"
        style={{ background: chip.fg }}
      />
      {chip.label}
    </span>
  );
}

/**
 * `fetchUsers` is injectable so the page can be rendered without a session.
 * The default is the real service, so nothing about production changes; the
 * alternative was letting a preview fire an unauthenticated request, which the
 * apiClient answers by logging out and navigating to /login.
 */
export default function PeopleRedesign({ fetchUsers = getUsers }) {
  const [users, setUsers] = useState([]);
  const [state, setState] = useState({ loading: true, error: null });
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null); // { mode: "add" | "edit", user }
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [statusError, setStatusError] = useState(null);

  // Which writes this client's backend actually has. Read once: it depends on
  // the logged-in client, not on anything the page does.
  const writes = useMemo(() => resolveUserWriteEndpoints(), []);
  const activeClient = useMemo(() => getClientEntry(getSourceName()), []);
  const userForm = activeClient?.userForm;
  const permissionOptions = userForm?.permissions || [];
  const showPermissionsColumn = permissionOptions.length > 0;
  // The action column only needs to be wider than an icon-button's 44px when
  // there's a second, text-labelled action (Suspend/Activate) next to Pencil —
  // which today is Hegic alone, via writes.status.
  const tableGridStyle = {
    gridTemplateColumns: [
      ...(showPermissionsColumn ? GRID_COLS_WITH_PERMISSIONS : GRID_COLS),
      writes.status ? "120px" : "44px",
    ].join(" "),
  };

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const data = await fetchUsers();
      setUsers(toUserRows(data, { activeFlagValue: activeClient?.activeFlagValue }));
      setState({ loading: false, error: null });
    } catch (error) {
      setState({
        loading: false,
        error:
          error?.response?.status === 404
            ? "This client's backend has no user list endpoint."
            : error?.message || "The user list could not be loaded",
      });
    }
  }, [fetchUsers, activeClient]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Suspend/activate — a narrower, separate capability from edit
   * (writes.status / setUserStatus), toggled in place rather than through
   * the edit modal.
   */
  const toggleUserStatus = async (user) => {
    if (!writes.status) return;
    const nextActive = !user.active;
    if (!window.confirm(`${nextActive ? "Re-activate" : "Suspend"} ${user.name}?`)) {
      return;
    }
    setStatusUpdatingId(user.id);
    setStatusError(null);
    try {
      await setUserStatus(user.id, nextActive);
      setUsers((prev) =>
        prev.map((entry) => (entry.id === user.id ? { ...entry, active: nextActive } : entry)),
      );
    } catch (caught) {
      console.error("Failed to update user status:", caught);
      setStatusError(`Could not update status for ${user.name}: ${caught?.message || "unknown error"}`);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const shown = useMemo(
    () => filterUsers(users, { query, role, status }),
    [users, query, role, status],
  );

  const activeCount = users.filter((user) => user.active).length;
  const rolesPresent = useMemo(
    () => [...new Set(users.map((user) => user.role).filter(Boolean))].sort(),
    [users],
  );

  return (
    <div className={shell}>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold leading-tight tracking-[-0.02em] text-cl-text">
            Users
          </h1>
          <p className="mt-[3px] text-[12.5px] text-cl-muted">
            {state.loading
              ? "Loading users…"
              : `${users.length} user${users.length === 1 ? "" : "s"} · ${activeCount} active`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ mode: "add", user: null })}
          disabled={!writes.add || state.loading}
          title={writes.add ? "Add a user" : describeUnsupportedUserWrite("add")}
          className="flex flex-none items-center gap-2 rounded-control bg-cl-accent px-4 py-[9px] text-[13px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          <UserPlus className="h-[15px] w-[15px]" />
          Add user
        </button>
      </div>

      {statusError && (
        <div className={`mb-4 ${WARN_BOX}`} style={WARN_STYLE}>
          <AlertTriangle
            className="mt-[1px] h-[13px] w-[13px] flex-none"
            style={{ color: "var(--cl-warn-chip-fg)" }}
          />
          <p className="text-[12px] leading-[1.5] text-cl-secondary">{statusError}</p>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-[10px]">
        <span className="relative">
          <Search className="pointer-events-none absolute left-[10px] top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-cl-faint" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, email or role"
            className={`${control} w-[268px] pl-[31px] placeholder:text-cl-faint`}
          />
        </span>
        <select
          value={role}
          onChange={(event) => setRole(event.target.value)}
          className={selectClass}
          style={selectArrowStyle}
        >
          <option value="all">All roles</option>
          {rolesPresent.map((entry) => (
            <option key={entry} value={entry}>
              {entry} ({users.filter((user) => user.role === entry).length})
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className={selectClass}
          style={selectArrowStyle}
        >
          <option value="all">Any status</option>
          <option value="active">Active ({activeCount})</option>
          <option value="inactive">Inactive ({users.length - activeCount})</option>
        </select>
        {(query || role !== "all" || status !== "all") && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setRole("all");
              setStatus("all");
            }}
            className="text-[12px] font-semibold text-cl-accent hover:underline"
          >
            Clear
          </button>
        )}
        {!state.loading && shown.length !== users.length && (
          <span className="text-[12px] text-cl-muted">
            {shown.length} of {users.length}
          </span>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-cl-card bg-cl-surface">
        <div
          className="hidden flex-none items-center border-b border-cl-head px-4 py-[10px] text-[10.5px] font-semibold uppercase tracking-[0.07em] text-cl-label lg:grid"
          style={tableGridStyle}
        >
          <div />
          <div>Name</div>
          <div>Email</div>
          <div>Role</div>
          {showPermissionsColumn && <div>Permissions</div>}
          <div>Status</div>
          <div>Last modified</div>
          <div />
        </div>

        {state.loading ? (
          <div className="flex flex-1 items-center justify-center gap-3 py-20 text-[12.5px] text-cl-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading users…
          </div>
        ) : state.error ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <div className="max-w-sm text-center">
              <p className="text-[14px] font-semibold text-cl-text">
                The user list could not be loaded
              </p>
              <p className="mt-1 text-[12.5px] text-cl-muted">{state.error}</p>
              <button
                type="button"
                onClick={load}
                className="mt-3 rounded-control border border-cl-control px-[13px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
              >
                Try again
              </button>
            </div>
          </div>
        ) : shown.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <div className="max-w-sm text-center">
              <p className="text-[14px] font-semibold text-cl-text">
                {users.length ? "No users match these filters" : "No users yet"}
              </p>
              <p className="mt-1 text-[12.5px] text-cl-muted">
                {users.length
                  ? "Clear the search or the filters to see everyone."
                  : "This client's user list came back empty."}
              </p>
            </div>
          </div>
        ) : (
          <div className="cl-scroll min-h-0 flex-1 overflow-y-auto">
            {shown.map((user) => {
              const permissionLabels = showPermissionsColumn
                ? permissionLabelsFor(user.permission, permissionOptions)
                : [];
              return (
              <div
                key={user.id}
                className="flex flex-col gap-[6px] border-b border-cl-row px-4 py-3 text-[12.5px] transition-colors last:border-b-0 hover:bg-cl-hover lg:grid lg:items-center lg:gap-0 lg:py-[11px]"
                style={tableGridStyle}
              >
                <div
                  className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-[10.5px] font-bold text-cl-secondary"
                  style={{ background: "var(--cl-avatar-bg)" }}
                  aria-hidden="true"
                >
                  {initialsOf(user.name)}
                </div>
                <div className="min-w-0 truncate font-semibold text-cl-text" title={user.name}>
                  {user.name}
                </div>
                <div className="min-w-0 truncate text-cl-secondary" title={user.email}>
                  {user.email || "—"}
                </div>
                <div className="min-w-0 truncate text-cl-secondary" title={user.role || ""}>
                  {user.role || "—"}
                </div>
                {showPermissionsColumn && (
                  <div
                    className="min-w-0 truncate text-cl-secondary"
                    title={permissionLabels.join(", ")}
                  >
                    {permissionLabels.length ? permissionLabels.join(", ") : "—"}
                  </div>
                )}
                <div>
                  <StatusPill active={user.active} />
                </div>
                <div className="text-cl-muted">{formatModified(user.lastModified)}</div>
                <div className="flex items-center justify-end gap-2">
                  {writes.status && (
                    <button
                      type="button"
                      onClick={() => toggleUserStatus(user)}
                      disabled={statusUpdatingId === user.id}
                      title={user.active ? `Suspend ${user.name}` : `Re-activate ${user.name}`}
                      className="whitespace-nowrap rounded-tile px-2 py-[3px] text-[11px] font-semibold uppercase text-cl-faint transition-colors hover:bg-cl-tint hover:text-cl-accent disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {statusUpdatingId === user.id ? "…" : user.active ? "Suspend" : "Activate"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditing({ mode: "edit", user })}
                    disabled={!writes.edit}
                    title={
                      writes.edit ? `Edit ${user.name}` : describeUnsupportedUserWrite("edit")
                    }
                    className="rounded-tile p-[6px] text-cl-faint transition-colors hover:bg-cl-tint hover:text-cl-accent disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Pencil className="h-[13px] w-[13px]" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <UserModal
          mode={editing.mode}
          user={editing.user}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Add or edit, in one dialog.
 *
 * Reloads the list on success rather than patching a row in place: the backend
 * decides the timestamp and the flag, and a locally invented row that disagrees
 * with the server is the kind of thing nobody notices until it matters.
 */
export function UserModal({ mode, user, onClose, onSaved }) {
  const activeClient = useMemo(() => getClientEntry(getSourceName()), []);
  const userForm = activeClient?.userForm;
  const roleOptions = userForm?.roles || ROLES;
  const permissionOptions = userForm?.permissions || [];
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role || roleOptions[0],
    active: user ? user.active : true,
    password: "",
    confirmPassword: "",
    permissions: Array.isArray(user?.permission)
      ? user.permission
      : String(user?.permission || "")
          .split(",")
          .map((code) => code.trim())
          .filter(Boolean),
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const togglePermission = (code) =>
    setForm((current) => ({
      ...current,
      permissions: current.permissions.includes(code)
        ? current.permissions.filter((entry) => entry !== code)
        : [...current.permissions, code],
    }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("A name and an email are both required.");
      return;
    }
    if (userForm && mode === "add" && (!form.password || form.password !== form.confirmPassword)) {
      setError("Password and confirm password must match.");
      return;
    }
    setError(null);

    // Only Hegic's backend has this route (activeClient.emailCheckEndpoint);
    // everywhere else this is skipped. A failed check doesn't block the add —
    // the add route itself is still the source of truth for a real duplicate.
    if (mode === "add" && activeClient?.emailCheckEndpoint) {
      setCheckingEmail(true);
      let emailExists = false;
      try {
        emailExists = await checkEmailExists(form.email.trim());
      } catch (caught) {
        console.error("Email existence check failed:", caught);
      } finally {
        setCheckingEmail(false);
      }
      if (emailExists) {
        setError("A user with this email already exists.");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = userForm
        ? {
            full_name: form.name.trim(),
            user_id: form.email.trim(),
            password: form.password,
            role: form.role.toLowerCase(),
            permissions: form.permissions,
            access_level: "0",
          }
        : {
            full_name: form.name.trim(),
            user_id: form.email.trim(),
            access_level: accessLevelForRole(form.role),
            flag: form.active ? "1" : "2",
            // Ergo motor only: editClientUser needs this round-tripped, not
            // guessed or blanked — this page has no control for it.
            ancAccessLevel: user?._raw?.anc_access_level,
          };
      if (mode === "add") {
        await addClientUser({ ...payload, client_id: "1" });
      } else {
        await editClientUser(user.id, payload);
      }
      onSaved();
    } catch (caught) {
      // Into the dialog, never into the page: an error that replaces the list
      // destroys the thing the reader was working on.
      setError(
        caught?.message
          ? `Could not ${mode === "add" ? "add" : "update"} the user: ${caught.message}`
          : `The user could not be ${mode === "add" ? "added" : "updated"}.`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-[rgba(19,23,32,0.45)] px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-[440px] overflow-hidden rounded-card border border-cl-card bg-cl-surface"
      >
        <div className="flex items-center justify-between border-b border-cl-head px-5 py-4">
          <h2 className="text-[15px] font-bold text-cl-text">
            {mode === "add" ? "Add a user" : "Edit user"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-tile p-1 text-cl-faint transition-colors hover:bg-cl-hover hover:text-cl-text"
          >
            <X className="h-[15px] w-[15px]" />
          </button>
        </div>

        <div className="space-y-[13px] px-5 py-[18px]">
          <Field label="Full name">
            <input
              type="text"
              value={form.name}
              onChange={(event) => set({ name: event.target.value })}
              className={`${control} w-full`}
              autoFocus
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={(event) => set({ email: event.target.value })}
              className={`${control} w-full`}
            />
          </Field>
          {userForm && mode === "add" && (
            <>
              <Field label="Password">
                <input
                  type="password"
                  value={form.password}
                  onChange={(event) => set({ password: event.target.value })}
                  className={`${control} w-full`}
                />
              </Field>
              <Field label="Confirm password">
                <input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(event) => set({ confirmPassword: event.target.value })}
                  className={`${control} w-full`}
                />
              </Field>
            </>
          )}
          <Field label="Role">
            <select
              value={form.role}
              onChange={(event) => set({ role: event.target.value })}
              className={`${selectClass} w-full`}
              style={selectArrowStyle}
            >
              {roleOptions.map((entry) => (
                <option key={entry} value={entry}>
                  {entry}
                </option>
              ))}
            </select>
          </Field>
          {permissionOptions.length > 0 && (
            <Field label="Permissions">
              <div className="flex flex-col gap-[6px]">
                {permissionOptions.map((option) => (
                  <label
                    key={option.code}
                    className="flex items-center gap-2 text-[12.5px] text-cl-secondary"
                  >
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(option.code)}
                      onChange={() => togglePermission(option.code)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </Field>
          )}
          {!(userForm && mode === "add") && (
            <Field label="Status">
              <div className="flex gap-2">
                {[
                  ["Active", true],
                  ["Inactive", false],
                ].map(([label, value]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => set({ active: value })}
                    className={`flex items-center gap-[6px] rounded-control border px-3 py-[7px] text-[12.5px] font-medium transition-colors ${
                      form.active === value
                        ? "border-cl-accent text-cl-accent"
                        : "border-cl-control text-cl-secondary hover:bg-cl-hover"
                    }`}
                  >
                    {form.active === value && <Check className="h-[12px] w-[12px]" />}
                    {label}
                  </button>
                ))}
              </div>
            </Field>
          )}

          {error && (
            <div className={WARN_BOX} style={WARN_STYLE}>
              <AlertTriangle
                className="mt-[1px] h-[13px] w-[13px] flex-none"
                style={{ color: "var(--cl-warn-chip-fg)" }}
              />
              <p className="text-[12px] leading-[1.5] text-cl-secondary">{error}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-cl-head px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control border border-cl-control px-4 py-[8px] text-[12.5px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || checkingEmail}
            className="flex items-center gap-2 rounded-control bg-cl-accent px-4 py-[8px] text-[12.5px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover disabled:opacity-60"
          >
            {(saving || checkingEmail) && <Loader2 className="h-[13px] w-[13px] animate-spin" />}
            {checkingEmail
              ? "Checking email…"
              : mode === "add"
                ? "Add user"
                : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-[5px] block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
        {label}
      </span>
      {children}
    </label>
  );
}
